/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { AuthStorage } from "../storage/auth.storage";
import { clearSessionState } from "../storage/session-state.storage";
import ResponseCodesEnum from "./enums/response-codes.enum";
import axios from "axios";
import { isInviteAcceptanceUrl } from "../../utils/invitation";
import { setupToastErrors } from "./apiToastError";
import { getApiBaseUrl } from "./api-service";
import { navigateTo } from "./navigator";
import { store } from "../../store/store";
import { logoutSuccess } from "../../store/actions/auth";
import i18n from "../../i18n";

const REFRESH_URL = "/user/token/refresh/";

// Django's LocaleMiddleware picks the language for translated error messages
// from Accept-Language. Without this the browser's own preference wins and an
// Italian browser gets Italian errors even with English selected in the UI.
// Read per request so a language switch takes effect immediately.
const applyLanguageHeader = (config) => {
  const language = i18n.language || i18n.options?.fallbackLng;
  if (language) {
    config.headers["Accept-Language"] = language;
  }
};

/** For axios instances that deliberately skip the full interceptor stack. */
export const attachLanguageHeader = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    applyLanguageHeader(config);
    return config;
  });
  return axiosInstance;
};

// A request is "the refresh call itself" — never try to refresh it again.
const isRefreshRequest = (config) => (config?.url || "").includes(REFRESH_URL);

// Once a refresh has failed, the session is unrecoverable until the user logs
// in again. Without this latch every component that 401s would kick off its own
// refresh, each failing and re-triggering logout — an endless 401 loop that
// never settles on the login page.
let sessionExpired = false;

/** Called after a successful login/refresh — re-arms the refresh machinery. */
export const markSessionActive = () => {
  sessionExpired = false;
};

/**
 * Called on explicit logout. Without this an in-flight request that 401s after
 * the tokens are cleared kicks off a refresh that cannot succeed — the cookie
 * is already gone — and its error surfaces as a toast on the login page.
 */
export const markSessionExpired = () => {
  sessionExpired = true;
};

export const forceLogout = () => {
  sessionExpired = true;
  AuthStorage.removeAccessToken();
  AuthStorage.removeUser();
  // Same cleanup as the explicit logout saga. This path matters more, not less:
  // a session that dies on a 401 mid-validation is exactly how a lock flag gets
  // stranded in the first place.
  clearSessionState();
  store.dispatch(logoutSuccess());
  // Guard against repeat navigation: concurrent 401s all land here, and
  // navigating to /login while already on /login remounts the page.
  if (window.location.pathname !== "/login") {
    navigateTo("/login");
  }
};

// In-flight refresh, shared by every caller. On page load a dozen requests can
// 401 at once; without this they would each POST /token/refresh/ and race to
// overwrite the stored token.
let refreshPromise = null;

/**
 * Exchange the httpOnly refresh cookie for a new access token.
 * Resolves with the token; rejects if the session is not recoverable.
 */
export const refreshSession = () => {
  if (sessionExpired) {
    return Promise.reject(new Error("Session expired; refresh not attempted"));
  }
  if (!refreshPromise) {
    const axiosInstance = axios.create({
      baseURL: getApiBaseUrl(),
      withCredentials: true,
    });
    refreshPromise = axiosInstance
      .post(REFRESH_URL, {}, {
        headers: {
          "ngrok-skip-browser-warning": true,
          ...(i18n.language ? { "Accept-Language": i18n.language } : {}),
        },
      })
      .then(({ data }) => {
        if (!data?.access) {
          throw new Error("Refresh response contained no access token");
        }
        AuthStorage.storeAccessToken(data.access);
        markSessionActive();
        return data.access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

/**
 * Called on app bootstrap. The access token lives in memory only, so after a
 * reload there is none — recover it from the refresh cookie before any other
 * request goes out. Resolves with the token, or null when there is no session.
 */
export const ensureAccessToken = async () => {
  if (AuthStorage.getAccessToken()) {
    return AuthStorage.getAccessToken();
  }
  try {
    return await refreshSession();
  } catch (e) {
    return null;
  }
};

// Retries through the same instance (not bare axios) so the response
// interceptor still runs — the Excel blob handling lives there. `_retried` on
// the config stops this from recursing.
const refreshAndRetry = async (lastRequest, axiosInstance) => {
  let accessToken;
  try {
    accessToken = await refreshSession();
  } catch (e) {
    setupToastErrors(e);
    forceLogout();
    throw e;
  }
  lastRequest.headers.Authorization = `Bearer ${accessToken}`;
  return await axiosInstance.request(lastRequest);
};

export function setupInterceptorsTo(axiosInstance) {
  const onRequest = async (config) => {
    const requestUrl = config?.url || "";
    const isMfaEndpoint =
      requestUrl.includes("/user/mfa/setup/") ||
      requestUrl.includes("/user/mfa/verify/") ||
      requestUrl.includes("/user/mfa/confirm/");
    let accessToken = AuthStorage.getAccessToken();

    // Check if this is an invite acceptance page
    const isInviteFlow = isInviteAcceptanceUrl();

    // Skip certain API requests during invite flow
    if (isInviteFlow) {
      // Block the files/counter API call which requires authentication
      if (config.url.includes("/files/counter")) {
        return Promise.reject(
          new axios.Cancel("Operation canceled for invite flow")
        );
      }

      // Skip auth header for all requests in invite flow
      config.headers["ngrok-skip-browser-warning"] = true;
      config.withCredentials = true;
      applyLanguageHeader(config);
      return config;
    }

    // The access token is memory-only, so right after a reload it is null while
    // the bootstrap refresh is still in flight. Firing now would send a request
    // with no Authorization header and eat a guaranteed 401 before the retry
    // recovers it. Instead, wait for the refresh already running (single-flight,
    // so this adds no extra network call). Only do this when a stored user says
    // a session should exist — on the login/signup pages there is nothing to
    // refresh. isRefreshRequest guards against recursing into ourselves.
    if (
      !isMfaEndpoint &&
      !accessToken &&
      !isRefreshRequest(config) &&
      AuthStorage.getUser()
    ) {
      accessToken = await ensureAccessToken();
    }

    // Normal auth flow for non-invite pages
    // MFA pre-auth endpoints authenticate via pre_auth_token only.
    if (!isMfaEndpoint && accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      delete config.headers.Authorization;
    }
    config.headers["ngrok-skip-browser-warning"] = true;
    config.withCredentials = true;
    applyLanguageHeader(config);
    return config;
  };

  const onRequestError = async (error) => {
    return await Promise.reject(error);
  };

  const onResponse = (response) => {
    const contentType = response.headers["content-type"];

    // Check if the response is an Excel file
    if (contentType && contentType.includes("application/excel")) {
      const disposition = response.headers["content-disposition"];
      let fileName = "download.xlsx"; // default name

      // Extract filename from Content-Disposition header
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, "");
        }
      }

      const blobFile = response.data;

      response.data = {};

      response.data.file = blobFile;
      response.data.fileName = fileName;
    }

    // Return the original response for other content types
    return response;
  };

  const onResponseError = async (error) => {
    // Check if request was intentionally cancelled
    if (axios.isCancel(error)) {
      setupToastErrors(error);
      return Promise.reject(error);
    }

    // Skip auth refresh for invite acceptance operations
    if (isInviteAcceptanceUrl()) {
      setupToastErrors(error);
      return await Promise.reject(error);
    }

    if (error.response?.status === ResponseCodesEnum.UNAUTHORIZED) {
      const lastRequest = error.config;

      // The refresh endpoint itself 401'd, or we already retried this request
      // with a fresh token and it still 401'd — the session is gone.
      if (!lastRequest || isRefreshRequest(lastRequest) || lastRequest._retried) {
        setupToastErrors(error);
        forceLogout();
        return await Promise.reject(error);
      }

      // No access token in memory is the normal state right after a reload, so
      // this is a case to refresh, not to bail out of. No toast here — the 401
      // is recoverable and invisible to the user when the retry succeeds;
      // refreshAndRetry surfaces the error itself if it doesn't.
      lastRequest._retried = true;
      return await refreshAndRetry(lastRequest, axiosInstance);
    }

    setupToastErrors(error);
    return await Promise.reject(error);
  };

  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);

  return axiosInstance;
}
